import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import { PortalOptionValueModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['portalOptionValues']

type PortalOptionValuesFilters = {
  portal_option_id?: number,
}

interface UsePortalOptionValuesOptions {
  entityKey?: string,
  filters?: PortalOptionValuesFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function usePortalOptionValues(options: UsePortalOptionValuesOptions = {}) {
  const { filters } = options
  const {
    portal_option_id: filterPortalOptionId,
  } = filters || {}

  const {
    updatedEntities: { portalOptionValues: portalOptionValuesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const portalOptionValues = useSelector(reduxState => reduxState.entities.portalOptionValues)

  const filteredPortalOptionValues = useMemo((): PortalOptionValueModel[] => {
    const filtered = Object.values(portalOptionValues).filter((portalOptionValue) => {
      const { portal_option_id } = portalOptionValue

      const matchPortalOption = matchFilterNumber(portal_option_id, filterPortalOptionId)

      return matchPortalOption
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [portalOptionValuesUpdatedAt, JSON.stringify(filters)])

  const filteredPortalOptionValuesCount = filteredPortalOptionValues.length
  const hasFilteredPortalOptionValues = !!filteredPortalOptionValuesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPortalOptionValuesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
  } = loadMorePayload

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPortalOptionValues,
    filteredPortalOptionValuesCount,
    hasFilteredPortalOptionValues,
  }
}

export default usePortalOptionValues
